import React, { useEffect, useState, useContext, useCallback } from 'react';

import { capitalize, Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BenefitsMetrics from './sections/BenefitsMetrics/BenefitsMetrics';
import ChallengesMetrics from './sections/ChallengesMetrics/ChallengesMetrics';
import CollaboratorsMetrics from './sections/CollaboratorsMetrics/CollaboratorsMetrics';
import DonationsMetrics from './sections/DonationsMetrics/DonationsMetrics';
import ProtectionMetrics from './sections/ProtectionMetrics/ProtectionMetrics';
import { ReactComponent as CalendarMonth } from '../../assets/icons/v3/calendar-month.svg';
import BdsSegment from '../../shared/components/v3/BdsSegment/BdsSegment';
import BdsButton from '../../shared/components/v3/Buttons/BdsButton/BdsButton';
import HelpUsImproveBanner from '../../shared/components/v3/HelpUsImproveBanner/HelpUsImproveBanner';
import PrimaryHero from '../../shared/components/v3/Heros/PrimaryHero/PrimaryHero';
import TimerSelector from '../../shared/components/v3/TimerSelector/TimerSelector';
import { REPORTS_ROUTE } from '../../shared/constants/auth';
import { NORTH_EAST_ARROW } from '../../shared/constants/images';
import {
  METRICS_TABS,
  METRICS_TIMER_SELECTOR,
  PERIOD_LAST_THREE_MONTHS,
  PERIOD_SELECTED_MONTH
} from '../../shared/constants/metrics';
import { CAN_SEE_INSURANCE_METRICS_FG } from '../../shared/constants/statsig';
import { CLICK_ACTION, GO_TO_REPORTS, SECTION_METRICS_V3, SELECT_TAB } from '../../shared/constants/tagManager';
import { CompanyContext } from '../../shared/context/company/companyContext';
import { MetricsContext } from '../../shared/context/metrics/metricsContext';
import { useIsMounted } from '../../shared/hooks/useIsMounted';
import useQueryParams from '../../shared/hooks/useQueryParams/useQueryParams';
import { useScrollState } from '../../shared/hooks/useScrollState/useScrollState';
import { IMetricsProtectionByCases, IMetricsQueryParams, IPeriodSelection } from '../../shared/interfaces/metrics';
import MetricsByCompany from '../../shared/services/api/metrics/metricsByCompany/metrics';
import TagManagerService from '../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_METRICS_NE } from '../../shared/translations/metrics';
import { getMonthName, redirectToPage } from '../../shared/utils/utils';
import { isEmptyObject } from '../../shared/utils/validation/validation';

import './MetricsNE.scss';

const MetricsNE = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type } = useQueryParams<IMetricsQueryParams>();
  const { periodSelection, changePeriodSelection } = useContext(MetricsContext);
  const { company, isSpain, isUSA } = useContext(CompanyContext);
  const { client } = useStatsigClient();

  const [selectedType, setSelectedType] = useState<string | undefined>(type);
  const [showTimerSelector, setShowTimerSelector] = useState(false);
  const [loadingProtectionResponse, setLoadingProtectionResponse] = useState<boolean>(true);
  const [protectionData, setProtectionData] = useState<IMetricsProtectionByCases>({} as IMetricsProtectionByCases);
  const [errorProtectionResponse, setErrorProtectionResponse] = useState<boolean>(false);
  const isMounted = useIsMounted();

  const canSeeInsuranceMetrics = client.checkGate(CAN_SEE_INSURANCE_METRICS_FG);
  const canSeeProtectionTab = !isUSA && canSeeInsuranceMetrics && !isEmptyObject(protectionData);
  const goToReportsTextButton = isSpain
    ? TRANSLATIONS_METRICS_NE.header.goToReport.rf
    : TRANSLATIONS_METRICS_NE.header.goToReport.default;
  const primaryHeroTitle = isSpain
    ? TRANSLATIONS_METRICS_NE.header.title.rf
    : TRANSLATIONS_METRICS_NE.header.title.default;

  const getProtectionByCases = useCallback(async () => {
    if (isUSA) {
      if (isMounted) setLoadingProtectionResponse(false);
      return;
    }
    setLoadingProtectionResponse(true);
    setErrorProtectionResponse(false);
    try {
      const response = await MetricsByCompany.getProtectionsByCaseMetrics(
        company.id,
        periodSelection.period,
        periodSelection.month
      );
      if (isMounted) setProtectionData(response);
    } catch {
      if (isMounted) setErrorProtectionResponse(true);
    } finally {
      if (isMounted) setLoadingProtectionResponse(false);
    }
  }, [company.id, periodSelection, isMounted, isUSA]);

  const isScrolled = useScrollState(0);

  const itemsTabbar = canSeeProtectionTab ? METRICS_TABS : METRICS_TABS.filter((tab) => tab.type !== 'protection');
  const isHiddenProtectionTab = selectedType === 'protection' && isUSA;
  const resolvedTabIndex = itemsTabbar.findIndex((tab) => tab.type === selectedType);
  const tabIndex = resolvedTabIndex !== -1 && !isHiddenProtectionTab ? resolvedTabIndex : 0;
  const selectedTabType = itemsTabbar[tabIndex].type;
  const metricsColor = itemsTabbar[tabIndex].color;
  const tabsItems = itemsTabbar.map((tab) => t(tab.title));

  const isResolvingProtectionTab =
    selectedType === 'protection' && !isUSA && !canSeeProtectionTab && loadingProtectionResponse;

  const onTabChange = (index: number) => {
    const tab = itemsTabbar[index];
    setSelectedType(tab.type);
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_TAB, { tab: tab.gtmTab });
    window.history.pushState({}, '', `?type=${tab.type}`);
  };

  const handleTimerSelector = () => {
    setShowTimerSelector(!showTimerSelector);
  };

  const onMonthSelected = useCallback(
    (value: IPeriodSelection) => {
      changePeriodSelection(value);
      setShowTimerSelector(false);
    },
    [changePeriodSelection]
  );

  const periodSelected = () => {
    if (periodSelection.period === PERIOD_LAST_THREE_MONTHS) {
      return t(METRICS_TIMER_SELECTOR.buttonThreeMonths);
    } else {
      return t(METRICS_TIMER_SELECTOR.buttonTwelveMonths);
    }
  };

  const goToReports = () => {
    TagManagerService.pushEvent(CLICK_ACTION, GO_TO_REPORTS, SECTION_METRICS_V3);
    redirectToPage(navigate, REPORTS_ROUTE);
  };

  useEffect(() => {
    getProtectionByCases();
  }, [periodSelection, company, getProtectionByCases]);

  const getPeriodSelector = () => {
    const isMonthlyOnlyTab =
      (selectedTabType === 'protection' && canSeeInsuranceMetrics && !isEmptyObject(protectionData)) ||
      selectedTabType === 'challenges';
    return (
      <div className="metrics-hero-content">
        <div className="period-selector me-6">
          <BdsButton iconLeft={{ component: CalendarMonth }} variant="secondary" size="m" action={handleTimerSelector}>
            {periodSelection.period === PERIOD_SELECTED_MONTH
              ? capitalize(getMonthName(periodSelection.month ?? ''))
              : periodSelected()}
          </BdsButton>
          <TimerSelector
            showTimerSelector={showTimerSelector}
            initialValue={{ period: periodSelection.period, month: periodSelection.month }}
            setShowTimerSelector={setShowTimerSelector}
            onSelected={onMonthSelected}
            showAggregatedPeriods={!isMonthlyOnlyTab}
          />
        </div>
        <BdsButton iconLeft={NORTH_EAST_ARROW} variant="primary" size="m" action={goToReports}>
          {t(goToReportsTextButton)}
        </BdsButton>
      </div>
    );
  };

  const renderTabsContent = () => {
    if (loadingProtectionResponse) {
      return (
        <div className="skeletons flex flex-row">
          {[...Array(tabsItems.length).keys()].map((number: number) => (
            <Skeleton key={`skeleton-${number}`} className="skeleton" variant="rectangular" height={36} width={134} />
          ))}
        </div>
      );
    }

    return <BdsSegment items={tabsItems} activeIndex={tabIndex} action={onTabChange} fullWidth={true} />;
  };

  return (
    <>
      <div className="metrics-header">
        <PrimaryHero
          title={t(primaryHeroTitle)}
          description={t(TRANSLATIONS_METRICS_NE.header.description)}
          color={metricsColor}
          sticky={true}
          bottomContent={getPeriodSelector()}
        />
        {!isScrolled && (
          <div className="metrics-tabs">
            <div className={`tabbar ${loadingProtectionResponse ? 'loading' : ''} flex flex-row`}>
              {renderTabsContent()}
            </div>
          </div>
        )}
      </div>
      <div className="metrics-tabs-content">
        {!isScrolled && <HelpUsImproveBanner pageKey="metrics" />}
        {isResolvingProtectionTab ? (
          <Skeleton className="rounded-4xl" variant="rectangular" height={300} />
        ) : (
          <>
            {selectedTabType === 'collaborators' && <CollaboratorsMetrics />}
            {selectedTabType === 'benefits' && <BenefitsMetrics />}
            {selectedTabType === 'donations' && <DonationsMetrics />}
            {selectedTabType === 'protection' && (
              <ProtectionMetrics
                byCasesData={protectionData}
                error={errorProtectionResponse}
                loading={loadingProtectionResponse}
              />
            )}
            {selectedTabType === 'challenges' && <ChallengesMetrics />}
          </>
        )}
      </div>
    </>
  );
};

export default MetricsNE;
